/**
 * ManeuverVisualization component - Retro-styled mission control plot display
 *
 * Features:
 * - Displays mission_control.html plot in an iframe
 * - Retro NASA/mission control theme styling
 * - Full-screen mode toggle
 * - Loading, ready, and error states
 * - Subtle fade-in animation on load
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { Maximize2, Minimize2, AlertCircle, Radio } from 'lucide-react'

interface ManeuverVisualizationProps {
  /** URL to the mission_control.html plot */
  plotUrl: string
  /** Title displayed in header (default: "MISSION CONTROL") */
  title?: string
  /** Callback when full-screen mode is toggled */
  onFullScreen?: () => void
}

type LoadingStatus = 'loading' | 'ready' | 'error'

function StatusIndicator({ status }: { status: LoadingStatus }) {
  const statusConfig = {
    loading: {
      color: 'bg-amber-500',
      pulse: true,
      label: 'LOADING',
    },
    ready: {
      color: 'bg-green-500',
      pulse: false,
      label: 'READY',
    },
    error: {
      color: 'bg-red-500',
      pulse: false,
      label: 'ERROR',
    },
  }

  const config = statusConfig[status]

  return (
    <div className="flex items-center gap-2">
      <div className={`relative w-2 h-2 rounded-full ${config.color}`}>
        {config.pulse && (
          <div
            className={`absolute inset-0 rounded-full ${config.color} animate-ping`}
          />
        )}
      </div>
      <span
        className="text-xs font-mono uppercase tracking-wider"
        style={{ color: status === 'ready' ? '#66ff66' : '#ffcc66' }}
      >
        {config.label}
      </span>
    </div>
  )
}

function ManeuverVisualization({
  plotUrl,
  title = 'MISSION CONTROL',
  onFullScreen,
}: ManeuverVisualizationProps) {
  const [status, setStatus] = useState<LoadingStatus>('loading')
  const [isFullScreen, setIsFullScreen] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [isVisible, setIsVisible] = useState(false)
  const iframeRef = useRef<HTMLIFrameElement>(null)

  // Validate URL and trigger animation on mount
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Reset state when plotUrl changes
    setStatus('loading')
    setErrorMessage(null)

    // Validate URL format
    try {
      new URL(plotUrl, window.location.origin)
    } catch {
      setStatus('error')
      setErrorMessage('Invalid plot URL')
      return
    }

    // Trigger fade-in animation after a short delay
    const animationTimer = setTimeout(() => {
      setIsVisible(true)
    }, 100)

    return () => {
      clearTimeout(animationTimer)
    }
  }, [plotUrl])

  // Handle iframe load events
  const handleIframeLoad = useCallback(() => {
    setStatus('ready')
  }, [])

  const handleIframeError = useCallback(() => {
    setStatus('error')
    setErrorMessage('Failed to load mission control plot')
  }, [])

  // Toggle full-screen mode
  const toggleFullScreen = useCallback(() => {
    setIsFullScreen((prev) => !prev)
    onFullScreen?.()
  }, [onFullScreen])

  // Full-screen overlay
  if (isFullScreen) {
    return (
      <div
        className="fixed inset-0 z-50 flex flex-col"
        style={{ backgroundColor: '#0d0d0d' }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-4"
          style={{
            backgroundColor: '#1a1a1a',
            borderBottom: '1px solid #333333',
          }}
        >
          <div className="flex items-center gap-4">
            <Radio size={20} style={{ color: '#ffcc66' }} />
            <h2
              className="text-lg font-mono font-bold tracking-widest"
              style={{ color: '#ffcc66' }}
            >
              {title}
            </h2>
          </div>
          <div className="flex items-center gap-6">
            <StatusIndicator status={status} />
            <button
              onClick={toggleFullScreen}
              className="flex items-center gap-2 px-4 py-2 font-mono text-sm transition-colors rounded"
              style={{
                backgroundColor: '#333333',
                color: '#ffcc66',
                border: '1px solid #4d4d4d',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#4d4d4d'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#333333'
              }}
            >
              <Minimize2 size={16} />
              EXIT
            </button>
          </div>
        </div>

        {/* Plot content */}
        <div className="flex-1 relative">
          {status === 'error' ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <AlertCircle
                  size={48}
                  className="mx-auto mb-4"
                  style={{ color: '#ff6666' }}
                />
                <p
                  className="text-lg font-mono"
                  style={{ color: '#ff6666' }}
                >
                  {errorMessage || 'Failed to load visualization'}
                </p>
              </div>
            </div>
          ) : (
            <iframe
              ref={iframeRef}
              src={plotUrl}
              title={title}
              className="w-full h-full border-0"
              onLoad={handleIframeLoad}
              onError={handleIframeError}
              sandbox="allow-scripts allow-same-origin"
            />
          )}
        </div>
      </div>
    )
  }

  // Normal view (embedded in sidebar)
  return (
    <div
      className={`flex flex-col flex-1 min-h-0 rounded-lg overflow-hidden transition-opacity duration-500 ${
        isVisible ? 'opacity-100' : 'opacity-0'
      }`}
      style={{
        backgroundColor: '#0d0d0d',
        border: '1px solid #333333',
      }}
    >
      {/* Retro header */}
      <div
        className="flex items-center justify-between px-4 py-3"
        style={{
          backgroundColor: '#1a1a1a',
          borderBottom: '1px solid #333333',
        }}
      >
        <div className="flex items-center gap-3">
          <Radio size={16} style={{ color: '#ffcc66' }} />
          <h3
            className="text-sm font-mono font-bold tracking-widest uppercase"
            style={{ color: '#ffcc66' }}
          >
            {title}
          </h3>
        </div>
        <div className="flex items-center gap-4">
          <StatusIndicator status={status} />
          <button
            onClick={toggleFullScreen}
            className="p-2 rounded transition-colors"
            style={{
              backgroundColor: 'transparent',
              color: '#ffcc66',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#333333'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent'
            }}
            title="Full screen"
            aria-label="Toggle full screen"
          >
            <Maximize2 size={16} />
          </button>
        </div>
      </div>

      {/* Plot content area */}
      <div className="flex-1 relative min-h-0">
        {status === 'loading' && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              {/* Retro loading animation */}
              <div className="flex justify-center gap-1 mb-4">
                {[0, 1, 2, 3, 4].map((i) => (
                  <div
                    key={i}
                    className="w-2 h-8 rounded animate-pulse"
                    style={{
                      backgroundColor: '#ffcc66',
                      animationDelay: `${i * 150}ms`,
                      opacity: 0.3 + (i * 0.15),
                    }}
                  />
                ))}
              </div>
              <p
                className="text-sm font-mono tracking-wider"
                style={{ color: '#666666' }}
              >
                INITIALIZING TELEMETRY...
              </p>
            </div>
          </div>
        )}

        {status === 'error' && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center px-4">
              <AlertCircle
                size={32}
                className="mx-auto mb-3"
                style={{ color: '#ff6666' }}
              />
              <p
                className="text-sm font-mono"
                style={{ color: '#ff6666' }}
              >
                SIGNAL LOST
              </p>
              <p
                className="text-xs font-mono mt-2"
                style={{ color: '#666666' }}
              >
                {errorMessage}
              </p>
            </div>
          </div>
        )}

        <iframe
          ref={iframeRef}
          src={plotUrl}
          title={title}
          className={`w-full h-full border-0 transition-opacity duration-300 ${
            status === 'ready' ? 'opacity-100' : 'opacity-0'
          }`}
          onLoad={handleIframeLoad}
          onError={handleIframeError}
          sandbox="allow-scripts allow-same-origin"
          style={{ minHeight: '400px' }}
        />
      </div>

      {/* Retro footer with scan line effect */}
      <div
        className="flex items-center justify-between px-4 py-2 text-xs font-mono"
        style={{
          backgroundColor: '#1a1a1a',
          borderTop: '1px solid #333333',
          color: '#666666',
        }}
      >
        <span>SYS: NOMINAL</span>
        <span className="tracking-widest">
          {new Date().toISOString().slice(0, 19).replace('T', ' ')} UTC
        </span>
      </div>
    </div>
  )
}

export default ManeuverVisualization
